// ═══════════════════════════════════════════════════════════════════════════
// La pantalla del superusuario de plataforma
//
// Solo la ve quien está en `platform_admin`, que se llena únicamente con acceso
// directo a la base. No hay forma de darse de alta a uno mismo y no debe
// haberla: una raíz de confianza con formulario deja de serlo.
//
// ── LO QUE ESTA PANTALLA NO ENSEÑA, A PROPÓSITO ────────────────────────────
//
// Ningún dato de nadie. Cuántas remisiones, sí; cuáles, no. Cuántas personas,
// sí; quiénes, no. Es la decisión D12: el superusuario no tiene membresía en
// ninguna vinícola, así que la RLS le devuelve cero filas de todo — se midió —
// y su único camino a los datos es entrar como alguien concreto, lo cual queda
// firmado.
//
// Se le puede decir a una vinícola: «nuestro personal no puede navegar sus
// datos; el acceso de soporte es una sesión explícita y registrada». Esta
// pantalla existe para que eso siga siendo cierto aunque haga falta operar.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../auth/sesion.js";
import { ligaDeTicket, mensajeDeInvitacion } from "../auth/ticket.js";
import { qrComoImagen } from "../dominio/qr.js";
import { parseCSF } from "../fiscal/csf.js";
import { evaluarCsfDeVinicola } from "../fiscal/actividades.js";
import { SAT_REGIMEN_FISCAL } from "../fiscal/catalogos-sat.js";
import { PlataformaVinicola } from "./PlataformaVinicola.jsx";
import { Card, Label, SectionTitle } from "./basicos.jsx";
import { btnPrimary, btnSecondary, inputStyle, selectStyle } from "./estilos.js";

// Las capacidades, con el nombre que se usa al hablar. El operador: "no se van
// a distinguir solo por el número de asientos, se van a distinguir también por
// tener varias bodegas, vendedores, tienda virtual, acceso de clientes".
//
// Estaban en la columna `features` de la tabla `plan` desde la migración 0006 y
// la pantalla solo enseñaba los asientos — el aspecto menos interesante de los
// tres. Aquí se nombran; los valores salen de la base.
const CAPACIDADES = [
  ["multi_almacen",  "Varias bodegas"],
  ["pedidos_campo",  "Pedidos en campo"],
  ["insumos",        "Insumos y producción"],
  ["estadisticas",   "Estadísticas"],
  ["portal_cliente", "Portal de clientes"],
  ["tienda_virtual", "Tienda virtual"],
];

// El catálogo del SAT, que ya vivía en `fiscal/catalogos-sat.js`. Tenía yo
// cuatro regímenes escritos a mano aquí, y eso rompe en cuanto una vinícola es
// persona física con un régimen que no adiviné — o en cuanto la CSF trae uno de
// los otros catorce y el selector no lo puede representar.

export function Plataforma() {
  const [vinicolas, setVinicolas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [alta, setAlta] = useState(false);
  const [nueva, setNueva] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [f, setF] = useState({ nombre: "", slug: "", rfc: "", razon: "",
                               regimen: "601", cp: "", plan: "basico",
                               serie: "", correo: "", dias: 14 });
  const [planes, setPlanes] = useState([]);
  const [csf, setCsf] = useState("");          // estado de la lectura de la CSF
  const [csfFuente, setCsfFuente] = useState(""); // "sat" si el QR se confirmó con el SAT
  // Qué dicen sus actividades económicas. `null` = todavía no se ha leído
  // una CSF; no es lo mismo que «no hace vino».
  const [giro, setGiro] = useState(null);
  // Cuando la CSF no acredita producción de vino, el alta EXIGE confirmación
  // explícita. No se bloquea: quien da de alta es el superusuario, que tiene
  // el documento delante, y un bloqueo insalvable dejaría fuera a una
  // vinícola legítima cuyo giro esté redactado de una forma que yo no
  // previera. Detectar, decirlo fuerte, y que confirme.
  const [asumoGiro, setAsumoGiro] = useState(false);
  const [fiscal, setFiscal] = useState(null);  // vinícola cuyo timbrado se configura
  const [estadoFiscal, setEstadoFiscal] = useState([]);
  // Qué falta por llenar. Se calcula siempre y se ENSEÑA solo al intentar
  // avanzar: marcar en rojo lo que alguien no ha tenido tiempo de escribir es
  // regañarlo por ir en orden.
  const [intento, setIntento] = useState(false);
  const faltan = {
    nombre: !f.nombre.trim(),
    razon: !f.razon.trim(),
    rfc: !f.rfc.trim(),
    regimen: !f.regimen.trim(),
    cp: !/^[0-9]{5}$/.test(f.cp.trim()),
  };
  const incompleto = Object.values(faltan).some(Boolean);

  const cargar = useCallback(async () => {
    setCargando(true); setError("");
    const [{ data, error: e }, { data: p }, { data: ef }] = await Promise.all([
      supabase.rpc("vinicolas_de_plataforma"),
      supabase.rpc("planes_disponibles"),
      supabase.rpc("estado_fiscal_de_plataforma"),
    ]);
    if (e) setError(e.message);
    else { setVinicolas(data || []); setPlanes(p || []); setEstadoFiscal(ef || []); }
    setCargando(false);
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const crear = async () => {
    setIntento(true);
    if (incompleto) {
      setError("Faltan datos obligatorios. Los que faltan están marcados abajo.");
      return;
    }
    if (giro && giro.veredicto !== "produccion" && !asumoGiro) {
      setError("La CSF no acredita producción de vino. Si aun así corresponde, "
        + "marca la casilla de abajo para dejarlo dicho.");
      return;
    }
    setOcupado(true); setError("");
    const { data, error: e } = await supabase.rpc("crear_vinicola", {
      p_nombre: f.nombre, p_slug: f.slug, p_rfc: f.rfc || null,
      p_razon_social: f.razon || null, p_regimen: f.regimen || null,
      p_cp: f.cp || null, p_plan: f.plan, p_serie: f.serie || null,
      p_correo_dueno: f.correo || null, p_dias_ticket: Number(f.dias) || 14,
    });
    if (e) setError(e.message);
    else {
      setNueva({ ...data, nombre: f.nombre });
      setF({ nombre: "", slug: "", rfc: "", razon: "", regimen: "601",
             cp: "", plan: "basico", serie: "", correo: "", dias: 14 });
      setAlta(false); setIntento(false); setCsf("");
      setGiro(null); setAsumoGiro(false);
      await cargar();
    }
    setOcupado(false);
  };

  return (
    <div>
      <SectionTitle sub={`${vinicolas.length} vinícola${vinicolas.length === 1 ? "" : "s"} en la plataforma`}>
        Plataforma
      </SectionTitle>

      {error && (
        <Card style={{ borderColor: "#3a1a1a" }}>
          <div style={{ color: "#f87171", fontSize: 13 }}>⚠️ {error}</div>
        </Card>
      )}

      {/* ── El ticket de la vinícola recién creada ────────────────────────
          Se enseña UNA vez, como todos los tickets. Con QR, porque el dueño
          nuevo va a estar enfrente o del otro lado de un mensaje. */}
      {nueva && (
        <Card style={{ borderColor: "#2a3a1a", background: "#101408" }}>
          <div style={{ fontSize: 11, color: "#a8d060", letterSpacing: 2, marginBottom: 10 }}>
            🍇 {nueva.nombre?.toUpperCase()} — DADA DE ALTA
          </div>
          <div style={{ display: "flex", justifyContent: "center", margin: "10px 0 6px" }}>
            <img src={qrComoImagen(ligaDeTicket(nueva.codigo), { modulo: 6, margen: 4 })}
              alt={`QR de la invitación ${nueva.codigo}`}
              style={{ width: 200, height: 200, borderRadius: 8, imageRendering: "pixelated" }} />
          </div>
          <div style={{ fontSize: 12, color: "#7a8a5a", lineHeight: 1.7, textAlign: "center" }}>
            Que el dueño lo apunte con su teléfono. Entra sin correo ni contraseña
            y queda como <b>propietario</b>.
            <br />Sirve una sola vez y vence el {nueva.vence}.
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            <button onClick={() => navigator.clipboard?.writeText(
                mensajeDeInvitacion(nueva.codigo, { vinicola: nueva.nombre, expira: nueva.vence }))}
              style={{ ...btnPrimary, padding: "9px 16px", fontSize: 12 }}>
              💬 Copiar mensaje
            </button>
            <button onClick={() => setNueva(null)}
              style={{ ...btnSecondary, marginLeft: "auto", padding: "9px 16px", fontSize: 12 }}>
              Cerrar
            </button>
          </div>
          <div style={{ fontSize: 11, color: "#666", marginTop: 10 }}>
            Cópialo ahora: al cerrar esto no se vuelve a mostrar.
          </div>
        </Card>
      )}

      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220, fontSize: 12, color: "#888", lineHeight: 1.6 }}>
            Aquí solo se ven <b style={{ color: "#aaa" }}>números</b>: cuántas
            personas, cuántas remisiones, si ya configuró su llave fiscal. Nunca
            los datos de nadie.
          </div>
          <button onClick={() => setAlta(a => !a)} style={btnPrimary}>
            {alta ? "Cancelar" : "+ Dar de alta una vinícola"}
          </button>
        </div>
      </Card>

      {alta && (
        <Card>
          {/* ── La CSF primero ───────────────────────────────────────────────
              El operador: "no veo dónde subir la CSF, es una parte importante".
              Y es la parte que EVITA teclear: de ahí salen RFC, razón social,
              régimen y código postal, que son justo los que no se pueden
              equivocar.

              Se lee y se DEJA VER antes de guardar nada. Una lectura automática
              que se aplica sola es una que nadie revisa, y un régimen fiscal mal
              leído timbra mal todos los CFDI de esa vinícola. */}
          <div style={{ padding: "14px 16px", background: "#0d1114",
                        border: "1px solid #1a2a3a", borderRadius: 8, marginBottom: 18 }}>
            <div style={{ fontSize: 11, color: "#70a0c9", letterSpacing: 1, marginBottom: 8 }}>
              📄 CONSTANCIA DE SITUACIÓN FISCAL
            </div>
            <input type="file" accept="application/pdf" disabled={ocupado}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setCsf("leyendo"); setError("");
                try {
                  // Con actividades: el giro de la vinícola se lee del texto,
                  // que el SAT no devuelve.
                  const d = await parseCSF(file, (m) => setCsf(m), { conActividades: true });
                  setF(prev => ({
                    ...prev,
                    rfc: d.rfc || prev.rfc,
                    razon: d.legalName || prev.razon,
                    regimen: d.taxSystem || prev.regimen,
                    cp: d.domicilioFiscalZip || d.cp || prev.cp,
                    nombre: prev.nombre || d.legalName || "",
                  }));
                  setGiro(evaluarCsfDeVinicola(d._lineas || []));
                  setAsumoGiro(false);
                  setCsfFuente(d._fuente || "pdf");
                  setCsf("listo");
                } catch (err) {
                  setCsf(""); setGiro(null); setAsumoGiro(false);
                  setError("No pude leer esa constancia: " + err.message +
                           ". Puedes capturar los datos a mano.");
                }
              }}
              style={{ fontSize: 12, color: "#888" }} />
            <div style={{ fontSize: 11, color: "#5a7a94", lineHeight: 1.7, marginTop: 8 }}>
              {csf === "listo"
                ? (csfFuente === "sat"
                    ? "✓ Leída y confirmada con el SAT. Revisa los campos de abajo antes de crear."
                    : "✓ Leída del PDF, sin confirmar con el SAT. Revisa los campos de abajo antes de crear: lo que se lea mal se timbra mal.")
                : csf
                  ? "Leyendo…"
                  : "Súbela y llena sola el RFC, la razón social, el régimen y el código postal. Opcional: también se puede capturar a mano."}
            </div>
          </div>

          {/* ── El giro, según sus actividades económicas ──────────────────
              El operador: "Para que una vinicola se pueda registrar su CSF
              debe incluir la producción de vino, etc." Y: "Pasa aún cuando la
              actividad no sea la principal."

              Se enseñan TODAS las actividades y no solo el veredicto. Quien da
              de alta tiene el documento delante: si el detector se equivoca
              —busca por texto, porque la CSF imprime la descripción y no la
              clave SCIAN— tiene que poder verlo, no fiarse de un semáforo. */}
          {giro && (
            <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 8,
                          background: giro.veredicto === "produccion" ? "#0d1a0d" : "#1a1408",
                          border: `1px solid ${giro.veredicto === "produccion" ? "#1e3a1e" : "#3a331a"}` }}>
              <div style={{ fontSize: 12, marginBottom: 6,
                            color: giro.veredicto === "produccion" ? "#4ade80" : "#facc15" }}>
                {giro.veredicto === "produccion" ? "✓ Acredita producción de vino" : "⚠️ No acredita producción de vino"}
              </div>
              <div style={{ fontSize: 11, color: "#8a8266", lineHeight: 1.7 }}>{giro.motivo}</div>

              {giro.actividades.length > 0 && (
                <div style={{ fontSize: 11, color: "#777", marginTop: 8 }}>
                  {giro.actividades.map((a, i) => (
                    <div key={i} style={{ padding: "2px 0" }}>
                      <span style={{ color: a.clase === "produccion" ? "#4ade80"
                                          : a.clase === "relacionada" ? "#c9a96e" : "#555" }}>
                        {a.clase === "produccion" ? "●" : a.clase === "relacionada" ? "◐" : "○"}
                      </span>{" "}
                      {a.descripcion} <span style={{ color: "#555" }}>· {a.porcentaje}%</span>
                    </div>
                  ))}
                </div>
              )}

              {giro.veredicto !== "produccion" && (
                <label style={{ display: "flex", gap: 8, alignItems: "flex-start",
                                marginTop: 10, fontSize: 11, color: "#c9a96e", cursor: "pointer" }}>
                  <input type="checkbox" checked={asumoGiro}
                    onChange={e => setAsumoGiro(e.target.checked)} disabled={ocupado}
                    style={{ marginTop: 2 }} />
                  <span>
                    Doy de alta esta vinícola de todos modos. Lo he comprobado en la
                    constancia y me hago cargo.
                  </span>
                </label>
              )}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <Campo etiqueta="Nombre de la vinícola" falta={intento && faltan.nombre}>
                <input value={f.nombre} onChange={e => setF({ ...f, nombre: e.target.value })}
                  style={borde(inputStyle, intento && faltan.nombre)} disabled={ocupado} />
              </Campo>
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <Campo etiqueta="Razón social" falta={intento && faltan.razon}
                nota="como aparece en la constancia">
                <input value={f.razon} onChange={e => setF({ ...f, razon: e.target.value })}
                  style={borde(inputStyle, intento && faltan.razon)} disabled={ocupado} />
              </Campo>
            </div>
            <Campo etiqueta="RFC" falta={intento && faltan.rfc}>
              <input value={f.rfc} onChange={e => setF({ ...f, rfc: e.target.value.toUpperCase() })}
                maxLength={13}
                style={{ ...borde(inputStyle, intento && faltan.rfc), fontFamily: "monospace" }}
                disabled={ocupado} />
            </Campo>
            <Campo etiqueta="Régimen fiscal" falta={intento && faltan.regimen}>
              <select value={f.regimen} onChange={e => setF({ ...f, regimen: e.target.value })}
                style={borde(selectStyle, intento && faltan.regimen)} disabled={ocupado}>
                {SAT_REGIMEN_FISCAL.map(r => (
                  <option key={r.code} value={r.code}>{r.code} · {r.label}</option>
                ))}
                {f.regimen && !SAT_REGIMEN_FISCAL.some(r => r.code === f.regimen) && (
                  <option value={f.regimen}>{f.regimen} · leído de la constancia</option>
                )}
              </select>
            </Campo>
            <Campo etiqueta="Código postal fiscal" falta={intento && faltan.cp}
              nota="cinco dígitos">
              <input value={f.cp} onChange={e => setF({ ...f, cp: e.target.value })}
                maxLength={5} style={borde(inputStyle, intento && faltan.cp)} disabled={ocupado} />
            </Campo>
            <Campo etiqueta="Serie de folios" nota="opcional">
              <input value={f.serie} onChange={e => setF({ ...f, serie: e.target.value.toUpperCase() })}
                maxLength={6} style={inputStyle} disabled={ocupado} />
            </Campo>
            <div style={{ gridColumn: "1/-1" }}>
              <Campo etiqueta="Identificador" nota="se arma solo con el nombre si lo dejas vacío">
                <input value={f.slug} onChange={e => setF({ ...f, slug: e.target.value })}
                  style={inputStyle} disabled={ocupado} />
              </Campo>
            </div>
          </div>

          {/* ── El plan, por lo que HACE ───────────────────────────────────── */}
          <div style={{ marginTop: 18 }}>
            <Label>Plan</Label>
            <div style={{ display: "grid", gap: 10,
                          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}>
              {planes.map(p => (
                <button key={p.code} type="button" onClick={() => setF({ ...f, plan: p.code })}
                  disabled={ocupado}
                  style={{ textAlign: "left", padding: "12px 14px", borderRadius: 10,
                           cursor: "pointer", fontFamily: "inherit",
                           background: f.plan === p.code ? "#1a1810" : "#141414",
                           border: `1px solid ${f.plan === p.code ? "#3a3222" : "#1e1e1e"}` }}>
                  <div style={{ fontSize: 13, color: f.plan === p.code ? "#c9a96e" : "#aaa",
                                fontWeight: 600, marginBottom: 6 }}>
                    {p.label}
                  </div>
                  <div style={{ fontSize: 10, color: "#666", marginBottom: 6 }}>
                    {p.max_seats == null ? "personas sin tope" : `hasta ${p.max_seats} personas`}
                  </div>
                  {CAPACIDADES.map(([clave, nombre]) => (
                    <div key={clave} style={{ fontSize: 10, lineHeight: 1.6,
                                              color: p.features?.[clave] ? "#8aa070" : "#444" }}>
                      {p.features?.[clave] ? "✓" : "·"} {nombre}
                    </div>
                  ))}
                </button>
              ))}
            </div>
          </div>

          <div style={{ fontSize: 11, color: "#666", lineHeight: 1.7, margin: "16px 0" }}>
            Al crearla sale un <b style={{ color: "#888" }}>ticket con QR</b> para
            que su dueño entre. La llave de Facturapi y el sello del SAT se
            configuran después, desde la ficha de cada vinícola.
          </div>

          <button onClick={crear} disabled={ocupado}
            style={{ ...btnPrimary, opacity: ocupado ? 0.4 : 1 }}>
            {ocupado ? "Creando…" : "Crear vinícola y generar su ticket"}
          </button>
        </Card>
      )}

      {cargando && <Card><div style={{ color: "#666", fontSize: 13 }}>Cargando…</div></Card>}

      {vinicolas.map(v => (
        <Card key={v.tenant_id} style={{ padding: "14px 16px",
                                         opacity: v.activa ? 1 : 0.6 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
            <span style={{ fontSize: 15, color: v.activa ? "#e8e0d4" : "#7a6a6a",
                           flex: 1, minWidth: 160 }}>
              {v.nombre}
              <span style={{ fontSize: 11, color: "#555", marginLeft: 8 }}>{v.slug}</span>
              {!v.activa && (
                <span style={{ fontSize: 10, color: "#f87171", marginLeft: 8 }}>· dada de baja</span>
              )}
            </span>
            <Dato n={v.personas} q="persona" qq="personas"
              aviso={v.asientos_tope != null && Number(v.asientos_usados) >= Number(v.asientos_tope)}
              extra={v.asientos_tope != null ? `de ${v.asientos_tope}` : null} />
            <Dato n={v.remisiones} q="remisión" qq="remisiones" />
            <Dato n={v.cfdi_vigentes} q="CFDI" qq="CFDI" />
            <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 4,
                           background: v.tiene_llave_fiscal ? "#0d1f0d" : "#1f1a0d",
                           color: v.tiene_llave_fiscal ? "#4ade80" : "#facc15" }}>
              {v.tiene_llave_fiscal
                ? (v.llave_es_produccion ? "timbra en producción" : "llave de pruebas")
                : "sin llave fiscal"}
            </span>
            <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 4,
                           background: "#111", color: "#888" }}>
              {v.plan}
            </span>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center",
                        flexWrap: "wrap", marginTop: 8 }}>
            <span style={{ fontSize: 11, color: "#666", flex: 1, minWidth: 200 }}>
              {Number(v.tickets_sin_usar) > 0 &&
                `${v.tickets_sin_usar} ticket(s) sin usar · `}
              {v.ultima_actividad
                ? `última actividad ${new Date(v.ultima_actividad).toLocaleDateString("es-MX")}`
                : "sin actividad todavía"}
            </span>
            {(() => {
              const F = estadoFiscal.find(x => x.tenant_id === v.tenant_id) || {};
              const falta = [!F.rfc && "RFC", !F.regimen_fiscal && "régimen",
                             !F.cp && "CP", !F.tiene_llave && "llave"].filter(Boolean);
              return falta.length ? (
                <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 4,
                               background: "#1f1a0d", color: "#facc15" }}>
                  para timbrar le falta: {falta.join(", ")}
                </span>
              ) : (
                <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 4,
                               background: "#0d1f0d", color: "#4ade80" }}>
                  {F.es_produccion ? "timbra en producción" : "timbra en pruebas"}
                </span>
              );
            })()}
            <button onClick={() => setFiscal(fiscal === v.tenant_id ? null : v.tenant_id)}
              style={{ ...btnSecondary, padding: "6px 13px", fontSize: 11,
                       borderColor: "#2a2a3a", color: "#a0a0c9" }}>
              {fiscal === v.tenant_id ? "Cerrar" : "Administrar"}
            </button>
          </div>

          {fiscal === v.tenant_id && (
            <div style={{ marginTop: 12 }}>
              <PlataformaVinicola
                v={v}
                fiscal={estadoFiscal.find(x => x.tenant_id === v.tenant_id)}
                planes={planes}
                onCambio={cargar}
                onCerrar={() => setFiscal(null)} />
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

/** Un campo con su etiqueta, y en rojo cuando falta. El obligatorio se dice
 *  con palabras y con color: el operador pidió que "no avance y marque que es
 *  obligatorio" en vez de insinuarlo con un texto de ejemplo dentro de la caja,
 *  que además desaparece en cuanto se escribe una letra. */
function Campo({ etiqueta, nota, falta, children }) {
  return (
    <div>
      <Label>
        {etiqueta}
        {nota && <span style={{ color: "#555" }}> · {nota}</span>}
        {falta && <span style={{ color: "#f87171" }}> · obligatorio</span>}
      </Label>
      {children}
    </div>
  );
}

const borde = (base, falta) =>
  falta ? { ...base, borderColor: "#5a2a2a" } : base;

function Dato({ n, q, qq, extra, aviso }) {
  return (
    <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 4,
                   background: aviso ? "#1f1a0d" : "#111",
                   color: aviso ? "#facc15" : "#888" }}>
      {n} {Number(n) === 1 ? q : qq}{extra ? ` ${extra}` : ""}
    </span>
  );
}
